import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, PenLine, Truck } from "lucide-react";
import { DocumentRow } from "../components/ui/DocumentRow";
import { EmptyState } from "../components/ui/EmptyState";
import { ListCapNotice } from "../components/ui/ListCapNotice";
import { PageHeader } from "../components/ui/PageHeader";
import { PortalState } from "../components/ui/PortalState";
import { apiGet, downloadBlob, PortalApiError } from "../api/portal-client";
import { usePortalResource } from "../hooks/usePortalResource";
import { DASH, formatDay, formatEuro, formatQuantity } from "../lib/format";

interface Shipment {
  trackingNumber: string | null;
  trackingUrl: string | null;
  trackingStatus: string | null;
  status: string;
  estimatedDeliveryDate: string | null;
}

interface BL {
  id: string;
  blNumber: string | null;
  createdAt: string;
  status: string;
  isSigned: boolean;
  lineCount: number;
  downloaded: boolean;
  transporteur: string | null;
  shipment: Shipment | null;
}

interface BlLine {
  id: string;
  productName: string;
  quantiteLivree: string | number | null;
  unite: string | null;
  totalHt: string | number | null;
  lotReference: string | null;
  annulee: boolean;
  gencode: string | null;
}

interface BlDetail {
  id: string;
  lines: BlLine[];
  transporteur: string | null;
  shipment: Shipment | null;
  order: { id: string; orderNumber: string | null } | null;
}

/** Plafond serveur de `/me/bl` (portal-auth.routes.ts). */
const BL_CAP = 100;

/**
 * Mes bons de livraison.
 *
 * W7-B — « Aucun BL pour l'instant » tombait dès la fin du chargement, coupure
 * réseau comprise. Le BL est la preuve de ce qui a été reçu : c'est le document
 * qu'un client ouvre justement quand il conteste une livraison, souvent depuis
 * une réserve mal couverte. L'état est nommé, le plafond de 100 est dit.
 */
export function DeliveryNotesPage() {
  const { data, status, error, reload } = usePortalResource<BL[]>(() => apiGet<BL[]>("/me/bl"));
  const [list, setList] = useState<BL[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, BlDetail>>({});
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    if (data) setList(data);
  }, [data]);

  async function handleDownload(bl: BL) {
    await downloadBlob(`/me/bl/${bl.id}/pdf`, `BL-${bl.blNumber ?? bl.id.slice(-8)}.pdf`);
    setList((prev) => prev.map((x) => (x.id === bl.id ? { ...x, downloaded: true } : x)));
  }

  async function toggleDetail(bl: BL) {
    if (expanded === bl.id) {
      setExpanded(null);
      return;
    }
    setExpanded(bl.id);
    setDetailError(null);
    if (details[bl.id]) return;
    try {
      const detail = await apiGet<BlDetail>(`/me/bl/${bl.id}`);
      setDetails((prev) => ({ ...prev, [bl.id]: detail }));
    } catch (e: unknown) {
      setDetailError(e instanceof PortalApiError ? e.message : "Détail indisponible.");
    }
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-6">
      <PageHeader
        title="Mes bons de livraison"
        subtitle="Le détail de ce qui vous a été livré, signature comprise."
      />

      <PortalState status={status} label="vos bons de livraison" error={error} onRetry={reload} />

      {status === "ready" && list.length === 0 && (
        <EmptyState
          icon={Truck}
          title="Aucun bon de livraison pour l'instant"
          description="Chaque livraison signée apparaîtra ici avec son détail et son PDF."
          action={{ to: "/orders", label: "Voir mes commandes" }}
        />
      )}

      {status === "ready" && list.length > 0 && (
        <>
          <div className="bg-card border border-border rounded-xl2 overflow-hidden">
            {list.map((bl) => (
              <div key={bl.id} className="border-b border-border last:border-0">
                <DocumentRow
                  title={bl.blNumber ?? bl.id.slice(-8)}
                  subtitle={`${formatDay(bl.createdAt)} — ${bl.lineCount} ligne${bl.lineCount > 1 ? "s" : ""}`}
                  downloaded={bl.downloaded}
                  onDownload={() => handleDownload(bl)}
                  rightExtra={
                    bl.isSigned ? (
                      <span className="shrink-0 flex items-center gap-1 text-xs text-success">
                        <PenLine size={14} /> Signé
                      </span>
                    ) : null
                  }
                />

                <div className="px-4 pb-3 -mt-1 flex flex-col gap-1.5">
                  {bl.transporteur && (
                    <p className="flex items-center gap-1.5 text-xs text-muted">
                      <Truck size={13} /> Transporteur : {bl.transporteur}
                    </p>
                  )}

                  {bl.shipment && (
                    <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
                      Expédition {bl.shipment.trackingStatus ?? bl.shipment.status}
                      {bl.shipment.trackingNumber ? ` — n°${bl.shipment.trackingNumber}` : ""}
                      {bl.shipment.estimatedDeliveryDate
                        ? ` — livraison estimée le ${formatDay(bl.shipment.estimatedDeliveryDate)}`
                        : ""}
                      {bl.shipment.trackingUrl && (
                        <a
                          href={bl.shipment.trackingUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-1 text-terracotta hover:underline"
                        >
                          Suivre <ExternalLink size={12} />
                        </a>
                      )}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={() => toggleDetail(bl)}
                    aria-expanded={expanded === bl.id}
                    className="inline-flex items-center gap-1 self-start text-xs text-muted hover:text-terracotta"
                  >
                    {expanded === bl.id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    Détail des lignes
                  </button>

                  {expanded === bl.id && (
                    <div className="mt-1">
                      {detailError && (
                        <p role="alert" className="text-xs text-danger">
                          {detailError}
                        </p>
                      )}
                      {!detailError && !details[bl.id] && (
                        <p role="status" className="text-xs text-muted">
                          Chargement du détail…
                        </p>
                      )}
                      {details[bl.id]?.lines.length === 0 && (
                        <p className="text-xs text-muted">Aucune ligne sur ce bon.</p>
                      )}
                      {details[bl.id] && details[bl.id].lines.length > 0 && (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-xs text-muted">
                              <th className="text-left font-medium py-1">Produit</th>
                              <th className="text-left font-medium py-1">Gencode</th>
                              <th className="text-right font-medium py-1">Qté</th>
                              <th className="text-right font-medium py-1">Total HT</th>
                            </tr>
                          </thead>
                          <tbody>
                            {details[bl.id].lines.map((l) => (
                              <tr key={l.id} className="border-t border-border">
                                <td className={`py-1.5 ${l.annulee ? "text-muted line-through" : "text-ink"}`}>
                                  {l.productName}
                                  {l.lotReference ? (
                                    <span className="text-xs text-muted"> — lot {l.lotReference}</span>
                                  ) : null}
                                </td>
                                <td className="py-1.5 text-xs font-mono text-muted">
                                  {l.gencode ?? DASH}
                                </td>
                                <td className="py-1.5 text-right text-muted">
                                  {formatQuantity(l.quantiteLivree, l.unite)}
                                </td>
                                <td className="py-1.5 text-right font-mono text-ink">
                                  {formatEuro(l.totalHt)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <ListCapNotice shown={list.length} cap={BL_CAP} label="bons de livraison" genre="m" />
        </>
      )}
    </main>
  );
}
